import moment from "moment";
import { useEffect } from "react";

import { BTLocalStorage } from "types/btStorage";

import { GetReadOnlyEmptyStateAccessMessage } from "utilities/emptyState/emptyStateUtility";
import { isInPortal } from "utilities/portal/portal";
import { routes } from "utilities/routes";

import { BTLinkRelative } from "commonComponents/btWrappers/BTLink/BTLink";
import {
    AutoDismissEntities,
    EmptyStateDataType,
    type IEmptyStateBannerOnlyEntityProps,
} from "commonComponents/entity/emptyState/common/EmptyState.api.types";
import {
    EmptyStateBanner,
    EmptyStateCallToActionButton,
    getEmptyStateBannerOnlyEntityDataType,
} from "commonComponents/entity/emptyState/EmptyStateBanner";

interface IJobPickerEmptyStateBannerProps extends IEmptyStateBannerOnlyEntityProps {
    canClearSearch: boolean;
    isBanner: boolean;
    mode: "Job" | "Template";
    isConnectedToAccounting: boolean;
}

const AUTO_DISMISS_DAYS = 14;

function getEmptyStateContent(props: IJobPickerEmptyStateBannerProps) {
    const { isReadonly, onCallToActionClick, mode } = props;

    if (isReadonly) {
        const addOrAnyVerbiage = isInPortal({ subs: true }) ? "any" : "add";
        return mode === "Job"
            ? GetReadOnlyEmptyStateAccessMessage("jobs", addOrAnyVerbiage)
            : GetReadOnlyEmptyStateAccessMessage("templates", addOrAnyVerbiage);
    }

    if (mode === "Job") {
        return <div>Add any jobs that you would like to start working on.</div>;
    }

    return (
        <>
            Streamline your process to more efficiently manage jobs with templates.
            <BTLinkRelative to={"/JobPickerActions" + routes.template.getTemplateDetailsLink(0)}>
                <EmptyStateCallToActionButton onCallToActionClick={onCallToActionClick}>
                    Create a {mode}
                </EmptyStateCallToActionButton>
            </BTLinkRelative>
        </>
    );
}

function getJobEmptyStateTitle(isReadonly: boolean | undefined, mode: "Job" | "Template") {
    if (mode !== "Job") {
        return "Save time with templates";
    }
    if (!isReadonly) {
        return "Get started here!";
    }
    return "Track job progress with Buildertrend";
}

export function JobPickerEmptyStateBanner(props: IJobPickerEmptyStateBannerProps) {
    const { isDismissed, isNewToEntity, onDismiss } = props;
    const isAlreadyDismissed = isDismissed || !isNewToEntity;

    useEffect(() => {
        // If the user has not already dismissed or is not new to the entity then check
        // local storage. If 14 days has passed then auto dismiss the banner
        if (isAlreadyDismissed) {
            return;
        }
        const autoDismissedDate = BTLocalStorage.get("bt-object-autoDismissEmptyState");
        if (autoDismissedDate[AutoDismissEntities.JobPicker]) {
            if (
                moment().diff(moment(autoDismissedDate[AutoDismissEntities.JobPicker]), "day") >=
                AUTO_DISMISS_DAYS
            ) {
                onDismiss!();
                delete autoDismissedDate[AutoDismissEntities.JobPicker];
                BTLocalStorage.set("bt-object-autoDismissEmptyState", autoDismissedDate);
            }
        } else {
            autoDismissedDate[AutoDismissEntities.JobPicker] = new Date().toISOString();
            BTLocalStorage.set("bt-object-autoDismissEmptyState", autoDismissedDate);
        }
        // Only run on mount, matching the previous componentDidMount behavior.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const dataType = getEmptyStateBannerOnlyEntityDataType(props);
    const isInitialDataState = dataType === EmptyStateDataType.InitialWithDataState;
    const hasFilteredDataAndIsBanner = props.hasFilteredData && props.isBanner;
    if (
        dataType === null ||
        ((isInitialDataState || hasFilteredDataAndIsBanner) && isAlreadyDismissed)
    ) {
        return null;
    }

    if (
        props.isBanner &&
        dataType !== EmptyStateDataType.EmptyState &&
        isInPortal({ subs: true })
    ) {
        return null;
    }

    if (dataType === EmptyStateDataType.FilteredState && !props.isBanner) {
        if (props.canClearSearch) {
            return (
                <div className="EmptyState m-1">
                    <EmptyStateBanner
                        title="Your search returned no results."
                        actionBeingPerformed={undefined}
                        isDismissable={false}
                        bordered={false}
                    >
                        Adjust your search to find what you're looking for.
                        {props.canClearSearch && (
                            <EmptyStateCallToActionButton
                                onCallToActionClick={props.onCallToActionClick}
                            >
                                Clear Search
                            </EmptyStateCallToActionButton>
                        )}
                    </EmptyStateBanner>
                </div>
            );
        }

        return (
            <div className="EmptyState m-1">
                <EmptyStateBanner
                    title="No results"
                    actionBeingPerformed={undefined}
                    isDismissable={false}
                    bordered={false}
                    data-testid="job-picker-filtered-empty-state"
                >
                    Adjust or clear your filters to find the {props.mode.toLocaleLowerCase()}s
                    you're looking for.
                </EmptyStateBanner>
            </div>
        );
    }

    return (
        <div className="EmptyState m-1">
            <EmptyStateBanner
                title={getJobEmptyStateTitle(props.isReadonly, props.mode)}
                actionBeingPerformed={props.actionBeingPerformed}
                onDismiss={onDismiss}
                isDismissable={isInitialDataState || props.hasFilteredData}
            >
                {getEmptyStateContent(props)}
            </EmptyStateBanner>
        </div>
    );
}
